import { Button, Space, Tooltip, Typography, theme as antdTheme } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { space, useNarrowViewport } from '@viq-admin/ui';

interface Props {
  summaryText: string;
  /** Opens the export dialog for the selected event. Omitted → no button. */
  onExport?:   () => void;
  disabled?:   boolean;
  /**
   * Opens the Salesforce export dialog. Rendered only when provided — the
   * console (cross-tenant `salesforceApi`) passes it; otherwise the button is
   * absent. The portal's former own-company variant (which independently
   * disabled/relabeled this button) was retired in p3 Task 6.
   */
  onExportSalesforce?: () => void;
  /**
   * Opens the move-leads dialog. Rendered only when provided — a shell with a
   * wired `moveApi` passes it; otherwise the button is absent (portal-safe).
   */
  onMove?: () => void;
  /** When set, the move button is disabled and this is shown as its tooltip. */
  moveDisabledReason?: string;
  /**
   * Receives the element right of Export CSV where a screen portals its own
   * primary action (the unified CRM grid's Send button, port plan step 6).
   * Rendered only while `hasPrimaryAction` is true, so the footer never holds
   * an empty slot. Omitted → the footer is exactly as before.
   */
  actionSlotRef?: (el: HTMLElement | null) => void;
  /**
   * Something occupies the action slot right now. Export CSV then steps down
   * to an outlined button so the two don't compete.
   */
  hasPrimaryAction?: boolean;
}

/**
 * Batch footer — ported from the portal's `src/views/BatchScreen/BatchFooter.tsx`.
 * Renders the summary strip plus a primary "Export CSV" button that opens the
 * shared ExportDialog, and — only when `onExportSalesforce` is supplied — a
 * "Salesforce" button that opens the cross-tenant Salesforce push dialog, and —
 * only when `onMove` is supplied — a "Move to conference" button (the console's
 * move-leads affordance), and — only while `actionSlotRef` + `hasPrimaryAction`
 * are supplied — a slot right of Export CSV for a screen's own primary action.
 * Keeps the layout frame identical to the portal.
 */
export function BatchFooter({
  summaryText,
  onExport,
  disabled,
  onExportSalesforce,
  onMove,
  moveDisabledReason,
  actionSlotRef,
  hasPrimaryAction,
}: Props) {
  const { token } = antdTheme.useToken();
  const narrow = useNarrowViewport();
  const slotOccupied = !!actionSlotRef && !!hasPrimaryAction;
  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        // A phone can't fit the console's three buttons on one line, so the
        // bar grows to wrap them rather than clipping them.
        ...(narrow
          ? { minHeight: 56, padding: `${space.sm}px ${space.md}px`, flexWrap: 'wrap' as const, gap: space.sm }
          : { height: 76, padding: '16px 24px' }),
        background: token.colorBgContainer,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      {/* The event's name is already the page heading; on a phone the room
          goes to the buttons. */}
      {!narrow && <Typography.Text>{summaryText}</Typography.Text>}
      {(onExport || onExportSalesforce || onMove || slotOccupied) && (
        <Space wrap={narrow} style={narrow ? { marginLeft: 'auto' } : undefined}>
          {onExportSalesforce && (
            <Button onClick={onExportSalesforce} disabled={disabled}>
              Push to Salesforce
            </Button>
          )}
          {onMove && (
            <Tooltip title={moveDisabledReason}>
              <span>
                <Button onClick={onMove} disabled={!!moveDisabledReason}>
                  Move to another event
                </Button>
              </span>
            </Tooltip>
          )}
          {onExport && (
            <Button
              {...(slotOccupied ? { color: 'primary' as const, variant: 'outlined' as const } : { type: 'primary' as const })}
              icon={<DownloadOutlined />}
              onClick={onExport}
              disabled={disabled}
            >
              Export CSV
            </Button>
          )}
          {slotOccupied && (
            <span
              ref={actionSlotRef}
              data-testid="batch-footer-action-slot"
              style={{ display: 'inline-flex' }}
            />
          )}
        </Space>
      )}
    </div>
  );
}
